import { FieldDef } from "./field";
import { adminRpc } from "../../services/admin-rpc-client";
import { uuidv7 } from "uuidv7";

/**
 * The result of a Dataset.save — decoupled from adminRpc's internal MutationResponse
 * so the form layer doesn't depend on the transport shape.
 */
export type SaveResult = {
  success: boolean;
  error?: { message?: string };
  /** the key of the row written (a freshly-minted uid on insert, or the edited key) */
  key: string;
};

/**
 * A Dataset binds a model/cube to its fields + record read/write — the
 * datasets+fields foundation for the form framework (FEATURE-20260708-form-framework).
 *
 * A form is a Dataset (one record) + a selected field list rendered via
 * FieldDef.getFormItem; the SAME dataset+fields also drive a table via
 * getDisplayComponent. Backed by the single-table adminRpc CRUD — NOT a query DSL.
 *
 * Field metadata (types/required/enum/validation) is hand-authored on the FieldDefs
 * for now; the cube -> frontend metadata bridge (the framework follow-on) will
 * populate them from the backend cube automatically.
 */
export interface Dataset {
  /** sql table / cube name (e.g. "job") */
  readonly cube: string;
  /** the field definitions (the authorable columns of this model) */
  readonly fields: FieldDef[];
  /** primary-key column; defaults to "uid" */
  readonly keyField: string;
  /** the field by name */
  field(name: string): FieldDef | undefined;
  /** read one record by key; undefined key or not-found -> undefined (create mode) */
  load(key: string | undefined): Promise<Record<string, unknown> | undefined>;
  /** insert (no key) or update (key). Returns success/error + the key written. */
  save(values: Record<string, unknown>, key: string | undefined): Promise<SaveResult>;
}

/**
 * A model: a named map of fields. The KEY is how you reference a field in the
 * form API (e.g. JobDef.concurrency_policy) — no string names at the call site.
 */
export type FieldModel = Record<string, FieldDef>;

export type DatasetOptions = {
  cube: string;
  model: FieldModel;
  /** primary-key column; default "uid" */
  keyField?: string;
  /**
   * Columns to treat as jsonb: their values are JSON.stringify'd on save (the
   * structured dbaccess write takes jsonb as a JSON string, per monitor-form.ts).
   * Reference by field (JobDef.payload_json) or name.
   */
  jsonColumns?: (FieldDef | string)[];
  /** Values written on insert for columns the form did not send (e.g. showWhen-hidden ones). */
  insertDefaults?: Record<string, unknown>;
};

const escSql = (s: string) => s.replace(/'/g, "''");
const newUid = () => uuidv7().replaceAll("-", "");

/**
 * A Dataset backed by the single-table adminRpc CRUD — the existing write path used
 * by monitor-form.ts. load = SELECT the field columns; save = insert/update through
 * the SQL firewall's structured dbaccess API (so cube @validate rules still fire).
 */
export function adminRpcDataset(opts: DatasetOptions): Dataset {
  const keyField = opts.keyField ?? "uid";
  const fields = Object.values(opts.model);
  const jsonCols = new Set(
    (opts.jsonColumns ?? []).map((c) => (typeof c === "string" ? c : c.name)),
  );
  const byName = new Map(fields.map((f) => [f.name, f]));
  const columns = fields.map((f) => f.name);

  return {
    cube: opts.cube,
    fields,
    keyField,
    field: (name) => byName.get(name),

    async load(key) {
      if (!key) return undefined;
      const selectCols = Array.from(new Set([keyField, ...columns])).join(", ");
      const resp = await adminRpc.query(
        `SELECT ${selectCols} FROM ${opts.cube} WHERE ${keyField} = '${escSql(String(key))}'`,
      );
      return resp.rows[0];
    },

    async save(values, key) {
      const row: Record<string, unknown> = {};
      for (const f of fields) {
        if (!(f.name in values)) continue;
        let v = values[f.name];
        // jsonb columns take a JSON string on the structured firewall write
        if (jsonCols.has(f.name) && v != null && typeof v !== "string") {
          v = JSON.stringify(v);
        }
        row[f.name] = v;
      }
      if (key) {
        const resp = await adminRpc.update(opts.cube, { [keyField]: key }, row);
        return { success: resp.success, error: resp.error, key };
      }
      const newKey = newUid();
      const resp = await adminRpc.insert(opts.cube, {
        [keyField]: newKey,
        ...opts.insertDefaults,
        ...row,
      });
      return { success: resp.success, error: resp.error, key: newKey };
    },
  };
}
